import React, { useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const clamp2 = (n) => Number(Math.max(0, Number(n) || 0).toFixed(2));

const formatMoney = (code, val) => `${code} ${clamp2(val).toFixed(2)}`;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const statusLabel = (s) => {
  // You can map these properly if you have exact enum. For now:
  // 1 = Active (most likely). Anything else = Inactive/Blocked/etc.
  if (Number(s) === 1) return { text: 'Active', cls: 'text-bg-success' };
  if (Number(s) === 0) return { text: 'Inactive', cls: 'text-bg-secondary' };
  return { text: `Status ${s}`, cls: 'text-bg-warning' };
};

/**
 * GiftCardStatusPanel (Cashier Display Only)
 *
 * Props:
 * - Currency?.currency?.code: string e.g. "PKR"
 * - previousAmount: number (amount due before using this gift card)
 * - title?: string
 * - autoLookupOnScan?: boolean (default true)
 */
export default function GiftCardStatusPanel({ previousAmount = 0, title = 'Gift Card Status', autoLookupOnScan = true }) {
  debugger;
  const inputRef = useRef(null);
  const scanTimerRef = useRef(null);
  const lookupSeqRef = useRef(0);
  const Currency = useSelector((state) => state?.Currency); // or state?.currency (see note below)

  const [codeInput, setCodeInput] = useState('');
  const [loading, setLoading] = useState(false);

  // null = not checked, true/false after check
  const [valid, setValid] = useState(null);
  const [data, setData] = useState(null); // your `result`

  const prev = useMemo(() => clamp2(previousAmount), [previousAmount]);

  const currentBalance = useMemo(() => clamp2(data?.currentBalance), [data]);
  const initialAmount = useMemo(() => clamp2(data?.initialAmount), [data]);

  // informational "new due if full balance used"
  const newAmount = useMemo(() => clamp2(prev - currentBalance), [prev, currentBalance]);

  const expired = useMemo(() => {
    if (!data?.expiryDate) return false;
    const d = new Date(data.expiryDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() < Date.now();
  }, [data?.expiryDate]);

  const reset = () => {
    setCodeInput('');
    setLoading(false);
    setValid(null);
    setData(null);
    setTimeout(() => inputRef.current?.focus(), 120);
  };

  const lookup = async (raw) => {
    const input = String(raw ?? codeInput).trim();
    if (!input) return toast.warning('Scan / enter gift card code first.');

    setLoading(true);
    setValid(null);
    setData(null);

    const seq = ++lookupSeqRef.current;

    try {
      const apiService = new ApiService();

      // If it starts with GC => giftCardCode else treat as barcode
      const looksLikeGiftCardCode = /^GC[-_]?/i.test(input);
      const paramName = looksLikeGiftCardCode ? 'giftCardCode' : 'barcode';

      const res = await apiService.getApiParamater('CHECK_GIFT_CARD', `?${paramName}=${encodeURIComponent(input)}`);

      if (seq !== lookupSeqRef.current) return;

      const gc = res?.data?.result;
      if (!gc) {
        setValid(false);
        setData(null);
        toast.error('Invalid / not found gift card.');
        return;
      }

      setData(gc);
      setValid(true);
      toast.success('Gift card verified.');
    } catch (err) {
      if (seq !== lookupSeqRef.current) return;
      console.error('Gift card lookup failed:', err);
      setValid(false);
      setData(null);
      toast.error(err?.response?.data?.message || 'Gift card lookup failed.');
    } finally {
      if (seq === lookupSeqRef.current) setLoading(false);
    }
  };

  const onInputChange = (v) => {
    setCodeInput(v);

    if (!autoLookupOnScan) return;

    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);

    const trimmed = String(v || '').trim();
    const scanLike = /^\d{6,}$/.test(trimmed) || /^GC[-_]?/i.test(trimmed);
    if (!scanLike) return;

    scanTimerRef.current = setTimeout(() => lookup(trimmed), 220);
  };

  const ValidBadge = () => {
    if (valid === null) {
      return (
        <span className="badge rounded-pill text-bg-secondary" style={{ padding: '6px 10px', fontSize: 12 }}>
          Not checked
        </span>
      );
    }
    if (valid) {
      return (
        <span className="badge rounded-pill text-bg-success" style={{ padding: '6px 10px', fontSize: 12 }}>
          Valid
        </span>
      );
    }
    return (
      <span className="badge rounded-pill text-bg-danger" style={{ padding: '6px 10px', fontSize: 12 }}>
        Invalid
      </span>
    );
  };

  const st = statusLabel(data?.status);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 120);
  }, []);

  return (
    <div className="border rounded-4 p-3" style={{ background: '#f8f9fa' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div style={{ fontWeight: 900, fontSize: 14, color: '#343a40', letterSpacing: 0.3 }}>{title}</div>
        <ValidBadge />
      </div>

      {/* Input */}
      <div className="row g-2 align-items-end">
        <div className="col-md-8">
          <label className="form-label fw-bold mb-1">Gift Card Code / Barcode</label>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            value={codeInput}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                lookup(e.currentTarget.value);
              }
            }}
            placeholder="Scan barcode or type code and press Enter"
            style={{
              borderRadius: 12,
              padding: '12px 14px',
              fontWeight: 800,
              letterSpacing: 0.3,
            }}
          />
        </div>

        <div className="col-md-4 d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary w-100"
            disabled={loading}
            onClick={() => lookup(codeInput)}
            style={{ borderRadius: 12, padding: '12px 14px', fontWeight: 900 }}
          >
            {loading ? 'Checking…' : 'Check'}
          </button>

          <button type="button" className="btn btn-outline-secondary" onClick={reset} style={{ borderRadius: 12, padding: '12px 14px', fontWeight: 900 }}>
            Clear
          </button>
        </div>
      </div>

      {/* Result */}
      <div className="mt-3 rounded-4 border bg-white p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: '#6c757d', letterSpacing: 0.4 }}>CARD DETAILS</div>

            <div style={{ fontSize: 14, fontWeight: 950, color: '#212529' }}>{valid === true ? data?.giftCardCode : '—'}</div>

            <div className="text-muted" style={{ fontSize: 12 }}>
              Barcode: <b>{valid === true ? data?.barcode : '—'}</b>
            </div>

            <div className="mt-2 d-flex gap-2 flex-wrap">
              <span className={`badge rounded-pill ${st?.cls || 'text-bg-secondary'}`} style={{ padding: '6px 10px', fontSize: 12 }}>
                {valid === true ? st.text : '—'}
              </span>

              <span
                className={`badge rounded-pill ${valid === true && expired ? 'text-bg-danger' : 'text-bg-secondary'}`}
                style={{ padding: '6px 10px', fontSize: 12 }}
              >
                Expiry: {valid === true ? '--' : '—'}
                {/* {valid === true && expired ? ' (Expired)' : ''} */}
              </span>

              {valid === true && (
                <span className="badge rounded-pill text-bg-light" style={{ padding: '6px 10px', fontSize: 12, color: '#212529' }}>
                  ID: {data?.giftCardId}
                </span>
              )}
            </div>
          </div>

          {/* Big Balance */}
          <div className="text-end">
            <div style={{ fontSize: 12, fontWeight: 900, color: '#6c757d', letterSpacing: 0.4 }}>CURRENT BALANCE</div>
            <div style={{ fontSize: 26, fontWeight: 980, color: valid ? '#198754' : '#adb5bd' }}>
              {valid ? formatMoney(Currency?.currency?.code, currentBalance) : formatMoney(Currency?.currency?.code, 0)}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Initial: <b>{valid ? formatMoney(Currency?.currency?.code, initialAmount) : formatMoney(Currency?.currency?.code, 0)}</b>
            </div>
          </div>
        </div>

        <hr className="my-3" />

        {/* Amount tiles */}
        <div className="row g-3">
          <div className="col-md-6">
            <div className="p-3 rounded-4" style={{ background: '#f8f9fa' }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: '#6c757d', letterSpacing: 0.4 }}>PREVIOUS AMOUNT</div>
              <div style={{ fontSize: 22, fontWeight: 980, color: '#212529' }}>{formatMoney(Currency?.currency?.code, prev)}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Amount due before this gift card
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="p-3 rounded-4" style={{ background: valid ? '#e7f1ff' : '#f8f9fa' }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: '#6c757d', letterSpacing: 0.4 }}>NEW AMOUNT</div>
              <div style={{ fontSize: 24, fontWeight: 990, color: valid ? '#0d6efd' : '#adb5bd' }}>
                {' '}
                {valid ? formatMoney(Currency?.currency?.code, currentBalance) : formatMoney(Currency?.currency?.code, 0)}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {valid ? 'Due after using full card balance' : 'Check card to calculate'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <div className="text-muted" style={{ fontSize: 12 }}>
            Tip: Scan and press <b>Enter</b> for fastest workflow.
          </div>
          {valid === true && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              Message: <b>Gift card balance fetched successfully</b>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
